import { apiClient } from "../../lib/apiClient";
import type {
  ActivationOverview,
  AssignStoreOwnerResult,
  DirectoryAuditPage,
  DirectoryDrift,
  LocationAdminListPage,
  LocationAdminsParams,
  LocationUser,
  LocationUserListPage,
  LocationUsersParams,
  ResendWelcomeResult,
  UnassignedStoreList,
  WorkvivoDirectoryRefreshProgress
} from "./types";

/** Drop undefined / empty-string values so they are not sent as `?q=`. */
function compact(params: Record<string, string | number | boolean | undefined>) {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    out[key] = value;
  }
  return out;
}

export async function fetchLocationAdmins(params: LocationAdminsParams): Promise<LocationAdminListPage> {
  const response = await apiClient.get<LocationAdminListPage>("/users/location-admins", {
    params: compact({
      q: params.q?.trim(),
      include_inactive: params.includeInactive ?? true,
      limit: params.limit,
      offset: params.offset
    })
  });
  return response.data;
}

export async function fetchLocationUsers(params: LocationUsersParams): Promise<LocationUserListPage> {
  const response = await apiClient.get<LocationUserListPage>("/users/location-users", {
    params: compact({
      owner_id: params.ownerId,
      store_id: params.storeId,
      q: params.q?.trim(),
      status: params.status ?? "all",
      limit: params.limit,
      offset: params.offset
    })
  });
  return response.data;
}

/** Superuser-only: Workvivo activation per active location plus overall totals. */
export async function fetchActivationOverview(): Promise<ActivationOverview> {
  const response = await apiClient.get<ActivationOverview>("/users/activation-overview");
  return response.data;
}

export async function refreshWorkvivoDirectory(): Promise<WorkvivoDirectoryRefreshProgress> {
  const response = await apiClient.post<WorkvivoDirectoryRefreshProgress>("/users/workvivo-directory/refresh");
  return response.data;
}

export async function fetchWorkvivoDirectoryStatus(): Promise<WorkvivoDirectoryRefreshProgress> {
  const response = await apiClient.get<WorkvivoDirectoryRefreshProgress>("/users/workvivo-directory/status");
  return response.data;
}

/** Deactivates a person in Workvivo. `confirmEmail` must match the user's email (the server re-checks it). */
export async function deactivateLocationUser(workvivoId: string, confirmEmail: string): Promise<LocationUser> {
  const response = await apiClient.post<LocationUser>(
    `/users/location-users/${encodeURIComponent(workvivoId)}/deactivate`,
    { confirm_email: confirmEmail }
  );
  return response.data;
}

/** Resets the admin's temporary password and emails new sign-in details. */
export async function resendWelcomeEmail(ownerId: string): Promise<ResendWelcomeResult> {
  const response = await apiClient.post<ResendWelcomeResult>(
    `/users/location-admins/${encodeURIComponent(ownerId)}/resend-welcome`
  );
  return response.data;
}

export async function fetchUnassignedStores(): Promise<UnassignedStoreList> {
  const response = await apiClient.get<UnassignedStoreList>("/users/location-admins/unassigned-stores");
  return response.data;
}

export async function assignStoreOwner(storeId: string, ownerId: string): Promise<AssignStoreOwnerResult> {
  const response = await apiClient.patch<AssignStoreOwnerResult>(`/stores/${encodeURIComponent(storeId)}`, {
    owner_id: ownerId
  });
  return response.data;
}

export async function fetchDirectoryDrift(): Promise<DirectoryDrift> {
  const response = await apiClient.get<DirectoryDrift>("/users/workvivo-directory/drift");
  return response.data;
}

export async function fetchDirectoryAudit(params: { limit: number; offset: number }): Promise<DirectoryAuditPage> {
  const response = await apiClient.get<DirectoryAuditPage>("/users/workvivo-directory/audit", {
    params: { limit: params.limit, offset: params.offset }
  });
  return response.data;
}

/** Filename from a `Content-Disposition: attachment; filename="..."` header, if present. */
function filenameFromContentDisposition(header: string | undefined): string | null {
  if (!header) return null;
  const starMatch = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (starMatch?.[1]) {
    try {
      return decodeURIComponent(starMatch[1].trim().replace(/^"|"$/g, ""));
    } catch {
      // fall through to the plain filename match below
    }
  }
  const plainMatch = /filename="?([^";]+)"?/i.exec(header);
  return plainMatch?.[1]?.trim() ?? null;
}

export interface ExportActiveLocationUsersParams {
  ownerId?: string;
  storeId?: string;
  q?: string;
}

/** Downloads the active-Workvivo-users CSV for the given scope and saves it via an object URL. */
export async function exportActiveLocationUsersCsv(params: ExportActiveLocationUsersParams): Promise<void> {
  const response = await apiClient.get<Blob>("/users/location-users/export.csv", {
    params: compact({
      owner_id: params.ownerId,
      store_id: params.storeId,
      q: params.q?.trim()
    }),
    responseType: "blob"
  });

  const today = new Date().toISOString().slice(0, 10);
  const fallbackFilename = `active-users-${today}.csv`;
  const filename = filenameFromContentDisposition(response.headers["content-disposition"]) ?? fallbackFilename;

  const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    window.URL.revokeObjectURL(url);
  }
}
